// R26 — pure Forever-set progress calculator. The server (submit_forever_set /
// get_my_forever, 20260924110000_forever_sets_server.sql) is the ONLY source of
// truth for definitions, XP and whether a submission succeeds; this module only
// tells the client which sets LOOK ready to submit and what to preselect, from
// the collection mirror's availability (copies - locked_copies, mirrored client
// side of the server's copies - locked_copies - open trade commitments — the
// server subtracts open trade commitments too, so a client-"ready" set can still
// come back set_incomplete; callers must treat the server's answer as final).
//
// No imports beyond the language itself — pure, dependency-free, unit-tested.

export interface ForeverCardDef {
  /** legacy_num — the prototype's global 1..306 card number. */
  num: number;
  /** Grouping key for team-based sets. Callers may use a team id or name —
   * this module never inspects the value beyond equality/sorting. */
  team: string;
}

/** The `definitions` shape returned by get_my_forever (R25), passed through
 * verbatim — no client-side XP table (R26). */
export interface ForeverSetDefinition {
  id: string;
  label: string;
  xp: number;
  description?: string | null;
  rarity: string | null;
  teams_required: number | null;
  per_team: number | null;
}

export type ForeverSelection =
  | Record<string, never> // common: {}
  | { legacy_num: number } // fullnum
  | { teams: string[] }; // silver/gold/platinum/ultimate

export interface ForeverSetProgress {
  id: string;
  have: number;
  total: number;
  pct: number;
  completable: boolean;
  /** Team-based sets only: teams that individually meet `per_team` at
   * `rarity`, ranked by greatest available distinct-card count descending
   * (ties broken alphabetically) so the suggestion favours the most-complete
   * teams (R26). Limited to these when building a team chooser. Empty for
   * common/fullnum. */
  completeTeams: string[];
  /** fullnum only: legacy_nums with all 5 rarities available, ascending.
   * Empty for every other set. */
  completableNumbers: number[];
  /** The most-complete pick, or null when not yet completable. */
  suggestedSelection: ForeverSelection | null;
}

const RARITIES = ["common", "silver", "gold", "platinum", "ultimate"] as const;

function compositeId(rarity: string, num: number): string {
  return `${rarity}-card-${num}`;
}

function hasCard(availability: Record<string, number>, rarity: string, num: number): boolean {
  return (availability[compositeId(rarity, num)] || 0) >= 1;
}

/**
 * Per-set progress, completable flag and a suggested selection, from the
 * collection mirror's availability. PURE — no Supabase, no mutation of any
 * input.
 *
 * - `common`: complete when every `cardDefs` entry has an available common
 *   copy; suggestedSelection is `{}` (the server needs no choice).
 * - `fullnum`: for each card number, counts how many of the 5 rarities are
 *   available; `completableNumbers` lists every number with all 5.
 *   suggestedSelection picks the lowest such number.
 * - silver/gold/platinum/ultimate: groups available cards of `rarity` by
 *   `team`; `completeTeams` are teams with at least `per_team` available
 *   distinct cards, ranked by greatest available distinct-card count
 *   descending (ties broken alphabetically). completable once there are at
 *   least `teams_required` complete teams; suggestedSelection takes the
 *   first `teams_required` of them — the most-complete teams (R26).
 */
export function foreverProgress(
  availability: Record<string, number>,
  cardDefs: ForeverCardDef[],
  defs: ForeverSetDefinition[],
): Record<string, ForeverSetProgress> {
  const avail = availability || {};
  const out: Record<string, ForeverSetProgress> = {};

  for (const def of defs || []) {
    if (def.id === "common") {
      const total = cardDefs.length;
      const have = cardDefs.filter((d) => hasCard(avail, "common", d.num)).length;
      const completable = total > 0 && have >= total;
      out[def.id] = {
        id: def.id,
        have,
        total,
        pct: total > 0 ? have / total : 0,
        completable,
        completeTeams: [],
        completableNumbers: [],
        suggestedSelection: completable ? {} : null,
      };
      continue;
    }

    if (def.id === "fullnum") {
      let bestHave = 0;
      const completableNumbers: number[] = [];
      for (const d of cardDefs) {
        const count = RARITIES.filter((r) => hasCard(avail, r, d.num)).length;
        if (count > bestHave) bestHave = count;
        if (count >= RARITIES.length) completableNumbers.push(d.num);
      }
      completableNumbers.sort((a, b) => a - b);
      const completable = completableNumbers.length > 0;
      out[def.id] = {
        id: def.id,
        have: bestHave,
        total: RARITIES.length,
        pct: bestHave / RARITIES.length,
        completable,
        completeTeams: [],
        completableNumbers,
        suggestedSelection: completable ? { legacy_num: completableNumbers[0] } : null,
      };
      continue;
    }

    // Team-based: silver / gold / platinum / ultimate.
    const rarity = def.rarity || "";
    const perTeam = def.per_team || 0;
    const teamsRequired = def.teams_required || 0;
    const byTeam = new Map<string, number>();
    for (const d of cardDefs) {
      if (!hasCard(avail, rarity, d.num)) continue;
      byTeam.set(d.team, (byTeam.get(d.team) || 0) + 1);
    }
    const teams = [...byTeam.keys()].sort();
    const completeTeams = teams
      .filter((t) => (byTeam.get(t) || 0) >= perTeam)
      .sort((a, b) => (byTeam.get(b) || 0) - (byTeam.get(a) || 0) || a.localeCompare(b));
    const bestCounts = teams
      .map((t) => Math.min(byTeam.get(t) || 0, perTeam))
      .sort((a, b) => b - a);
    const have = bestCounts.slice(0, teamsRequired).reduce((s, c) => s + c, 0);
    const total = perTeam * teamsRequired;
    const completable = teamsRequired > 0 && completeTeams.length >= teamsRequired;
    out[def.id] = {
      id: def.id,
      have,
      total,
      pct: total > 0 ? have / total : 0,
      completable,
      completeTeams,
      completableNumbers: [],
      suggestedSelection: completable ? { teams: completeTeams.slice(0, teamsRequired) } : null,
    };
  }

  return out;
}
